import React, { ReactNode, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';

import './ui.scss';

interface SpinnerProps {
  loading?: boolean;
  center?: boolean;
  /** Drop the default block margin so the spinner sits flush inside an icon slot/button. */
  inline?: boolean;
  children?: ReactNode;
  delay?: boolean;
}

export const Spinner: React.FC<SpinnerProps> = ({
  loading,
  center,
  inline,
  children,
  delay = false,
}) => {
  const { t, ready } = useTranslation(undefined, { useSuspense: false });

  const className = cx('spinner', {
    'spinner--center': center,
    'spinner--inline': inline,
  });

  // Delay spinner appearance if `delay` is enabled, so that it only appears when there is an actually long loading time.
  // This is useful to avoid flickering when loading is very quick.
  const [show, setShow] = useState(!delay && !!loading);
  useEffect(() => {
    let timer: number;
    if (delay && loading) {
      timer = window.setTimeout(() => setShow(true), 500);
    } else {
      setShow(!!loading);
    }
    return () => window.clearTimeout(timer);
  }, [loading, delay]);

  // Render nothing until delay elapsed when loading
  if (!loading) return <>{children}</>;
  if (loading && !show) return null;
  return (
    <div
      role="progressbar"
      aria-label={ready ? t('common.loading') : 'Loading'}
      className={className}
    />
  );
};

Spinner.defaultProps = {
  loading: true,
  delay: false,
};

Spinner.propTypes = {
  loading: PropTypes.bool,
  center: PropTypes.bool,
  inline: PropTypes.bool,
  children: PropTypes.node,
  delay: PropTypes.bool,
};

export default Spinner;
